import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { useIslands } from '@/lib/islands';
import {
  fetchKomunikatKonserwacji,
  fetchLimitRuchu,
  fetchOdstepKopii,
  fetchZegarNaprawiaj,
  fetchSyncTables,
  setKomunikatKonserwacji,
  setLimitRuchu,
  setOdstepKopii,
  setZegarNaprawiaj,
  setCyklSek,
  setGrupaCykli,
  type GrupaTabel,
  type SyncTable,
} from '@/lib/sync-tables-api';

/**
 * ODSTĘPY ODPYTYWANIA TABEL SPS.
 *
 * ⚠️ AGENT NIE MA REJESTRU ZMIAN PO STRONIE SQL SERVERA. W każdym cyklu czyta WSZYSTKIE śledzone
 * tabele i porównuje skróty z własnym obrazem; do portalu idą wyłącznie różnice, więc transferu
 * to nie dotyczy — ale odczyt kosztuje bazę klienta i jest jednakowy dla tabeli zmieniającej się
 * co minutę i dla tej, którą ktoś rusza raz na kwartał.
 *
 * ⚠️ LICZBA WIERSZY JEST TU CZĘŚCIĄ ODPOWIEDZI. Bez niej odstępy ustawia się na wyczucie.
 */

/**
 * ILE CYKLI WYPADA Z ODSTEPU W SEKUNDACH.
 *
 * ⚠️ `ceil`, NIE ZAOKRAGLENIE: agent czyta tabele w PIERWSZYM cyklu, w ktorym od ostatniego odczytu
 * minelo co najmniej `everySec` (`agent/src/config-diff/harmonogram.ts`). Przy cyklu 60 s odstep
 * 61 s to juz dwa cykle, a nie „prawie jeden" — i to jest cala odpowiedz na pytanie „co sie
 * zadzieje przy 80 s".
 *
 * ⚠️ ZOSTAJE, CHOC POLA W SEKUNDACH JUZ NIE MA. Ustawia sie teraz cykle na grupie, ale ta funkcja
 * jest JEDYNYM miejscem, ktore zamienia jedno na drugie — i to ona pilnuje, zeby napis obok pola
 * mowil to samo, co zrobi agent.
 */
export function cykliOpis(sek: number, cyklSek: number): string {
  if (!Number.isFinite(sek) || sek <= 0 || cyklSek <= 0) return 'w każdym cyklu';
  const cykli = Math.ceil(sek / cyklSek);
  if (cykli <= 1) return 'w każdym cyklu';
  return `co ${cykli}. cykl (~${cykli * cyklSek} s)`;
}

/**
 * WIERSZ TABELI — TYLKO DO ODCZYTU.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POLE ODSTĘPU ZNIKŁO Z WIERSZA I PRZESZŁO NA GRUPĘ (28.08.2026), na wprost zgłoszone:
 *  „liczba cykli do ustawienia tylko per grupa zależna od siebie i w jednakowym czasie (…) wewnątrz
 *  grupy jak dotychczas szczegóły każdej tabelki (ilość / ms tak jak teraz są)".
 *
 *  ⚠️ POWÓD JEST GŁĘBSZY NIŻ WYGODA: agent dociąga przodków tabeli, której czas nadszedł,
 *  NIEZALEŻNIE od ich odstępów — więc odstęp ustawiony na rodzicu bywał martwy (`UserGroups`
 *  z odstępem 24 h czytało się co cykl, bo co cykl czytało się `UserGroupMembership`). Jedna
 *  wartość na całą grupę znosi tę pułapkę: rodzic nigdy nie jest rzadszy niż dziecko.
 *
 *  ⚠️ LICZBY ZOSTAJĄ WSZYSTKIE. Wiersze i narzut to jedyne miejsce, po którym poznaje się, KTÓRA
 *  tabela zjada czas — a to pytanie zadaje się właśnie wtedy, gdy się ustawia odstęp grupy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function Wiersz({ t }: { t: SyncTable }) {
  return (
    <tr className="border-t border-border/60">
      <td className="py-1.5 pr-3">
        <span className="font-mono text-xs">{t.sps}</span>
        {t.kursorowa && (
          <span className="ml-1 text-[11px] text-muted-foreground" title="Czytana kursorem — tylko nowe wiersze">
            (kursor)
          </span>
        )}
        {t.wymaga.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            wymaga: <span className="font-mono">{t.wymaga.join(', ')}</span>
          </div>
        )}
      </td>
      <td className="py-1.5 pr-4 text-right tabular-nums text-muted-foreground">
        {t.wierszy === null ? '—' : t.wierszy}
      </td>
      <td className="py-1.5 pr-4 text-right tabular-nums text-muted-foreground">
        {t.narzutMs === null ? (
          <span title="Agent jeszcze nie przysłał pomiaru — średnie jadą raz na godzinę">—</span>
        ) : (
          <span title={`średnia z ${t.narzutPomiarow ?? 0} pomiarów`}>{t.narzutMs} ms</span>
        )}
      </td>
    </tr>
  );
}

/**
 * SEKCJA GRUPY — nagłówek z podsumowaniem i JEDNYM polem, rozwijana lista tabel pod nim.
 *
 * ⚠️ ZWINIĘTA DOMYŚLNIE, tak jak grupy w Uprawnieniach i z tego samego powodu: dwadzieścia pięć
 * wierszy rozwiniętych naraz to ekran, po którym trzeba przewijać, żeby zobaczyć same nazwy grup.
 * Nagłówek niesie wszystko, co potrzebne do decyzji — liczbę tabel, wierszy i zmierzony narzut.
 */
/**
 * DLUGOSC CYKLU AGENTA — jedno pole, obok nazwy.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ GRANICE 30–600 s NIE SA Z POWIETRZA i warto je znac, zanim ktos zapyta „a czemu nie 5 s":
 *  ponizej 30 s pelny odczyt WSZYSTKICH tabel konfiguracji uderza w produkcyjny SQL Server klienta
 *  czesciej, niz cokolwiek na tym ekranie jest warte; powyzej 600 s jeden nieudany przebieg
 *  (backoff do 15 minut) wpycha maszyne w alert „agent milczy", ktory portal zapala po 20 minutach.
 *  Czyli ustawienie z tego ekranu zapalaloby alarm na drugim.
 *
 *  ⚠️ DZIALA OD NASTEPNEGO SYGNALU ZYCIA, nie od kliknieca — i tak jest napisane obok pola.
 *  Agent przyjmuje wartosc w odpowiedzi na sygnal i stosuje ja do NASTEPNEJ pauzy, wiec przy
 *  dlugim cyklu zwloka siega kilku minut. Udawanie zmiany natychmiastowej konczylo by sie
 *  pytaniem „czemu nadal czyta co minute".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function CyklAgenta({
  cyklSek,
  mozeZapisywac,
  onZapisz,
}: {
  cyklSek: number;
  mozeZapisywac: boolean;
  onZapisz: (sek: number) => Promise<void>;
}) {
  const [wartosc, setWartosc] = useState(String(cyklSek));
  const [zapisuje, setZapisuje] = useState(false);

  const liczba = wartosc.trim() === '' ? null : Number(wartosc);
  const poprawne = liczba !== null && Number.isInteger(liczba) && liczba >= 30 && liczba <= 600;
  const zmienione = poprawne && liczba !== cyklSek;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-sm">
      <label className="text-xs text-muted-foreground" htmlFor="cykl-agenta">
        Cykl agenta
      </label>
      <Input
        id="cykl-agenta"
        className="h-8 w-20"
        type="number"
        min={30}
        max={600}
        value={wartosc}
        disabled={!mozeZapisywac}
        onChange={(e) => setWartosc(e.target.value)}
      />
      <span className="text-xs text-muted-foreground">
        s {poprawne ? '' : '(od 30 do 600, domyślnie 60)'}
      </span>
      {mozeZapisywac && zmienione && (
        <Button
          size="sm"
          className="h-7"
          disabled={zapisuje}
          onClick={() => {
            if (liczba === null) return;
            setZapisuje(true);
            void onZapisz(liczba).finally(() => setZapisuje(false));
          }}
        >
          {zapisuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          Zapisz
        </Button>
      )}
      <span className="text-xs text-muted-foreground">
        Wchodzi w życie przy najbliższym sygnale życia agenta.
      </span>
    </div>
  );
}

function GrupaSekcja({
  g,
  cyklSek,
  mozeZapisywac,
  otwarta,
  onPrzelacz,
  onZapisz,
}: {
  g: GrupaTabel;
  cyklSek: number;
  mozeZapisywac: boolean;
  otwarta: boolean;
  onPrzelacz: () => void;
  onZapisz: (cykli: number | null) => Promise<void>;
}) {
  const [wartosc, setWartosc] = useState(g.cykli === null ? '1' : String(g.cykli));
  const [zapisuje, setZapisuje] = useState(false);

  const liczba = wartosc.trim() === '' ? null : Number(wartosc);
  const poprawne = liczba !== null && Number.isInteger(liczba) && liczba >= 1 && liczba <= 43_200;
  const zmienione = poprawne && liczba !== (g.cykli ?? 1);

  const zapisz = async (): Promise<void> => {
    if (!poprawne) return;
    setZapisuje(true);
    try {
      await onZapisz(liczba === 1 ? null : liczba);
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <section className="rounded-md border border-border">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-muted/50 px-3 py-2">
        <button
          type="button"
          onClick={onPrzelacz}
          aria-expanded={otwarta}
          className="flex min-w-0 flex-1 items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight className={cn('size-3.5 shrink-0 transition-transform', otwarta && 'rotate-90')} aria-hidden />
          <span className="font-medium">{g.nazwa}</span>
          <span className="text-xs text-muted-foreground">
            {g.tabele.length} {g.tabele.length === 1 ? 'tabela' : g.tabele.length < 5 ? 'tabele' : 'tabel'}
            {g.wierszyRazem !== null && ` · ${g.wierszyRazem} wierszy`}
            {g.narzutMsRazem !== null && ` · ${g.narzutMsRazem} ms`}
          </span>
        </button>

        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor={`cykli-${g.klucz}`}>
            co ile cykli
          </label>
          <Input
            id={`cykli-${g.klucz}`}
            className="h-8 w-20"
            type="number"
            min={1}
            value={wartosc}
            disabled={!mozeZapisywac}
            onChange={(e) => setWartosc(e.target.value)}
          />
          {/*
            ⚠️ NAPIS LICZONY TĄ SAMĄ FUNKCJĄ, CO PRZELICZENIE ODSTĘPU (`cykliOpis`) — żeby to, co
            widzi człowiek, i to, co zrobi agent, nie mogło się rozjechać w dwóch miejscach.
          */}
          <span className="w-36 text-xs text-muted-foreground">
            {!poprawne ? 'od 1 do 43 200' : cykliOpis((liczba ?? 1) * cyklSek, cyklSek)}
          </span>
          {mozeZapisywac && zmienione && (
            <Button size="sm" className="h-7" disabled={zapisuje} onClick={() => void zapisz()}>
              {zapisuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Zapisz
            </Button>
          )}
        </div>
      </header>

      <div className="space-y-1 px-3 py-2">
        <p className="text-xs text-muted-foreground">{g.opis}</p>
        {/*
          ⚠️ TABELE MIESZANE TO STAN Z CZASÓW USTAWIEŃ PER TABELA i trzeba o nim powiedzieć PRZED
          zapisem: zapisanie grupy zrówna wszystkie jej tabele, także tam, gdzie ktoś kiedyś ustawił
          coś celowo. Cicha zamiana byłaby najgorszym wyjściem.
        */}
        {g.mieszane && (
          <p className="text-xs text-[var(--warning)]">
            Tabele tej grupy mają dziś <strong>różne</strong> odstępy (ustawienia sprzed grupowania). Zapisanie ustawi
            jeden odstęp dla wszystkich.
          </p>
        )}
        {/*
          ⚠️ KRAWĘDZIE MIĘDZYGRUPOWE MÓWIMY WPROST — to cena podziału po sensie zamiast po grafie.
          Lista przychodzi z portalu, więc pozostanie prawdziwa, gdy dojdzie nowa tabela.
        */}
        {g.dociagane.map((d) => (
          <p key={d.tabela} className="text-xs text-muted-foreground">
            <span className="font-mono">{d.tabela}</span> będzie czytana częściej, niż mówi ten odstęp — dociąga ją
            grupa {d.przezGrupy.join(' i ')}. Rodzic nigdy nie jest rzadszy niż dziecko, inaczej wiersze dzieci
            wpadałyby jako sieroty.
          </p>
        ))}
      </div>

      {otwarta && (
        <div className="overflow-x-auto border-t border-border px-3 pb-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 pr-3 font-medium">Tabela SPS</th>
                <th className="py-1 pr-4 text-right font-medium">Wierszy</th>
                <th
                  className="py-1 pr-4 text-right font-medium"
                  title="Średni czas zapytania do SPS z ostatniej godziny pracy agenta"
                >
                  Narzut
                </th>
              </tr>
            </thead>
            <tbody>
              {g.tabele.map((t) => (
                <Wiersz key={t.sps} t={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


export function OdstepyTabel() {
  const { groupId } = useIslands();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  const dane = useQuery({
    queryKey: ['sync-tables', groupId],
    queryFn: () => fetchSyncTables(groupId!),
    enabled: Boolean(groupId),
  });

  /**
   * KTORE GRUPY SA ROZWINIETE — domyslnie zadna, tak jak w Uprawnieniach.
   *
   * ⚠️ TRZYMAMY ROZWINIETE, NIE ZWINIETE: pusty zbior jest dokladnie stanem domyslnym, a zbior
   * „co zamkniete" wymagalby znajomosci pelnej listy grup.
   *
   * ⚠️⚠️ HOOK NAD BRAMKĄ NA WYSPĘ. `groupId` jest `null`, dopóki nie wczyta się lista wysp, więc
   * pierwszy render wychodził wyżej — a hook pod tym `return`em wykonywał się dopiero w drugim.
   * React przerywa wtedy całe drzewo i ekran gaśnie na biały (tak wyłożyły się „Moduły"
   * 02.09.2026). Pilnuje tego `hooki-nad-returnami.spec.ts`.
   */
  const [rozwiniete, setRozwiniete] = useState<ReadonlySet<string>>(new Set());

  if (!groupId) return null;
  const mozeZapisywac = user?.role === 'ADMIN';
  const przelacz = (klucz: string) =>
    setRozwiniete((byly) => {
      const teraz = new Set(byly);
      if (!teraz.delete(klucz)) teraz.add(klucz);
      return teraz;
    });

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div>
        <h2 className="font-semibold">Odświeżanie danych z bazy SPS</h2>
      </div>

      {/*
        ⚠️ JEDNA LINIJKA ZAMIAST TRZECH AKAPITÓW — zgłoszone wprost (28.08.2026): „wywal te
        wszystkie opisy (…) nie jest to jednak potrzebne. Tylko proste info «Cykl agenta i obok pole
        do ustawiania»".

        ⚠️ POLE JEST PRAWDZIWE, NIE OZDOBNE: od 28.08.2026 długość cyklu jest ustawieniem WYSPY,
        które agent przyjmuje w sygnale życia. Do tej pory żyła wyłącznie na maszynie
        (`AGENT_INTERVAL_SEC` w usłudze), a ekran pisał „60 s" jako założenie — czyli przeliczenia
        „co ile cykli" byłyby nieprawdziwe na maszynie ustawionej inaczej.
      */}
      <CyklAgenta
        cyklSek={dane.data?.cyklSek ?? 60}
        mozeZapisywac={mozeZapisywac}
        onZapisz={async (sek) => {
          try {
            await setCyklSek(groupId, sek);
            toast.success(`Cykl agenta: ${sek} s — wejdzie w życie przy najbliższym sygnale życia`);
            void qc.invalidateQueries({ queryKey: ['sync-tables'] });
          } catch (e) {
            toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
          }
        }}
      />

      {dane.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Wczytuję…
        </p>
      )}
      {dane.isError && <p className="text-sm text-[var(--destructive)]">{(dane.error as Error).message}</p>}

      {dane.data && (
        <div className="space-y-2">
          {dane.data.grupy.map((g) => (
            <GrupaSekcja
              key={g.klucz}
              g={g}
              cyklSek={dane.data.cyklSek}
              mozeZapisywac={mozeZapisywac}
              otwarta={rozwiniete.has(g.klucz)}
              onPrzelacz={() => przelacz(g.klucz)}
              onZapisz={async (cykli) => {
                try {
                  await setGrupaCykli(g.klucz, groupId, cykli);
                  toast.success(
                    cykli === null
                      ? `${g.nazwa}: w każdym cyklu (${g.tabele.length} tabel)`
                      : `${g.nazwa}: co ${cykli}. cykl (${g.tabele.length} tabel)`,
                  );
                  void qc.invalidateQueries({ queryKey: ['sync-tables'] });
                } catch (e) {
                  toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
                }
              }}
            />
          ))}

          {/*
            ⚠️ SUMA OBOK ZMIERZONEGO CZASU CAŁEGO ODCZYTU — te liczby NIE ZEJDĄ SIĘ co do
            milisekundy i to jest informacja, nie usterka. Różnica to narzut poza samymi
            zapytaniami: otwarcie połączenia, odczyt kształtu tabel, porównanie z obrazem.
          */}
          {/*
            ⚠️ PODSUMOWANIE W RAMCE, NIE SZARĄ LINIJKĄ — zgłoszone: „podsumowanie na dole daj jakoś
            bardziej widoczne". To trzy liczby, po których poznaje się, czy ustawienia wyżej mają
            w ogóle sens: suma zmierzonych zapytań, czas całej fazy odczytu i czas całego cyklu.

            ⚠️ TE LICZBY NIE ZEJDĄ SIĘ CO DO MILISEKUNDY i to jest informacja, nie usterka. Różnica
            to narzut poza samymi zapytaniami: otwarcie połączenia, odczyt kształtu tabel,
            porównanie z obrazem. Dlatego stoją obok siebie, a nie jedna zamiast drugiej.
          */}
          {dane.data.odczytMs !== null && (
            <div className="grid gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm sm:grid-cols-3">
              <div>
                <div className="text-xs text-muted-foreground">Suma zmierzonych zapytań</div>
                <div className="font-medium tabular-nums">
                  {Math.round(dane.data.rows.reduce((a, r) => a + (r.narzutMs ?? 0), 0) * 10) / 10} ms
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Faza odczytu tabel</div>
                <div className="font-medium tabular-nums">{dane.data.odczytMs} ms</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Cały cykl</div>
                <div className="font-medium tabular-nums">
                  {dane.data.cyklMs === null ? '—' : `${dane.data.cyklMs} ms`}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!mozeZapisywac && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Zmiana odstępów wymaga uprawnień administratora — skrócenie obciąża bazę produkcyjną klienta.
        </p>
      )}
    </section>
  );
}

/**
 * PRÓG RUCHU AGENTA NA DOBĘ — alarm, zanim zniknie pakiet.
 *
 * ⚠️ ISTNIEJE PO AWARII, NIE Z OSTROŻNOŚCI: agent na maszynie z LTE zjadł u klienta pakiet 150 GB
 * w dwa tygodnie i nikt tego nie widział, dopóki pakiet się nie skończył. Zmierzone: normalna praca
 * to ~32 MB na dobę, więc próg rzędu 200–500 MB odzywa się przy awarii, a milczy przy pracy,
 * w której zdarzają się kopie bazy.
 *
 * ⚠️ `0` ZNACZY „BEZ ALARMU" i jest wartością domyślną. Na łączu bez limitu alarm byłby szumem,
 * a szum uczy zamykać alerty bez czytania.
 */
function LimitRuchu({ groupId, mozeZapisywac }: { groupId: string; mozeZapisywac: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const dane = useQuery({ queryKey: ['limit-ruchu', groupId], queryFn: () => fetchLimitRuchu(groupId) });
  const [wartosc, setWartosc] = useState('');
  const [zapisuje, setZapisuje] = useState(false);

  useEffect(() => {
    if (dane.data) setWartosc(String(dane.data.limitMb));
  }, [dane.data]);

  const liczba = Number(wartosc);
  const poprawne = Number.isInteger(liczba) && liczba >= 0 && liczba <= 500_000;
  const zmienione = dane.data !== undefined && liczba !== dane.data.limitMb;

  const zapisz = async (): Promise<void> => {
    if (!poprawne) return;
    setZapisuje(true);
    try {
      await setLimitRuchu(groupId, liczba);
      toast.success(liczba === 0 ? 'Alarm o ruchu agenta wyłączony' : `Próg ruchu: ${liczba} MB na dobę`);
      void qc.invalidateQueries({ queryKey: ['limit-ruchu'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-sm">
      <span className="text-xs text-muted-foreground">Alarm, gdy agent przekroczy na dobę (MB):</span>
      <Input
        className="h-8 w-24"
        type="number"
        min={0}
        max={500000}
        value={wartosc}
        disabled={!mozeZapisywac}
        onChange={(e) => setWartosc(e.target.value)}
      />
      <span className="text-xs text-muted-foreground">
        {liczba === 0 ? 'bez alarmu' : 'zwykła praca to ~32 MB na dobę'}
      </span>
      {mozeZapisywac && zmienione && poprawne && (
        <Button size="sm" className="h-7" disabled={zapisuje} onClick={() => void zapisz()}>
          {zapisuje ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Zapisz'}
        </Button>
      )}
    </div>
  );
}

/**
 * CO ILE CYKLI AGENT LICZY SPIS KOPII NA DYSKU I WOLNE MIEJSCE.
 *
 * ⚠️ CYKLE, NIE SEKUNDY, i to nie jest niekonsekwencja wobec odstępów tabel wyżej. Ten spis dzieje
 * się WEWNĄTRZ cyklu agenta, więc jego naturalną jednostką jest cykl — a długość cyklu ustawia się
 * lokalnie na maszynie (`AGENT_INTERVAL_SEC`) i portal jej nie zna. Podanie sekund kazałoby ekranowi
 * przeliczać na coś, czego nie wie.
 *
 * ⚠️ DOMYŚLNE 10 to około dziesięciu minut i tak ma zostać. Spis wymaga sum kontrolnych plików po
 * kilkaset megabajtów, a wolne miejsce zmienia się wolno — pytanie co cykl kosztowałoby 1440
 * odczytów dziennie po to, żeby zauważyć zmianę, która następuje przy kopii albo ręcznym kasowaniu.
 */
function OdstepKopii({ groupId, mozeZapisywac }: { groupId: string; mozeZapisywac: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const dane = useQuery({ queryKey: ['odstep-kopii', groupId], queryFn: () => fetchOdstepKopii(groupId) });
  const [wartosc, setWartosc] = useState('');
  const [zapisuje, setZapisuje] = useState(false);

  useEffect(() => {
    if (dane.data) setWartosc(String(dane.data.coCykli));
  }, [dane.data]);

  const liczba = Number(wartosc);
  const poprawne = Number.isInteger(liczba) && liczba >= 1 && liczba <= 1440;
  const zmienione = dane.data !== undefined && liczba !== dane.data.coCykli;

  const zapisz = async (): Promise<void> => {
    if (!poprawne) return;
    setZapisuje(true);
    try {
      await setOdstepKopii(groupId, liczba);
      toast.success(`Spis kopii: co ${liczba} ${liczba === 1 ? 'cykl' : 'cykli'}`);
      void qc.invalidateQueries({ queryKey: ['odstep-kopii'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-sm">
      <span className="text-xs text-muted-foreground">Spis kopii na dysku maszyny — co ile cykli:</span>
      <Input
        className="h-8 w-20"
        type="number"
        min={1}
        max={1440}
        value={wartosc}
        disabled={!mozeZapisywac}
        onChange={(e) => setWartosc(e.target.value)}
      />
      <span className="text-xs text-muted-foreground">
        {poprawne ? `≈ ${liczba} min przy cyklu minutowym` : 'od 1 do 1440 cykli'}
      </span>
      {mozeZapisywac && zmienione && poprawne && (
        <Button size="sm" className="h-7" disabled={zapisuje} onClick={() => void zapisz()}>
          {zapisuje ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Zapisz'}
        </Button>
      )}
    </div>
  );
}

/**
 * KOREKTA ZEGARA MASZYNY — jedyna rzecz, którą agent zmienia w SYSTEMIE klienta poza bazą SPS.
 *
 * ⚠️ PRZENIESIONE Z `agent.json`. Ustawienie istniało od początku, ale wyłącznie w pliku na maszynie
 * — czyli włączenie wymagało wejścia na maszynę klienta, a dotyczy dokładnie tych maszyn, na które
 * wchodzi się najrzadziej. Zgłoszone: „ponoć gdzieś miało być naprawienie czasu na maszynie".
 *
 * ⚠️ CO TO ROBI NAPRAWDĘ, napisane przy przełączniku, a nie w dokumentacji: agent przestawia zegar
 * względem czasu portalu, odmawia przy rozjeździe większym niż doba (bo to nie dryf, tylko padła
 * bateria albo zły rok) i przestaje po trzech korektach pod rząd, żeby nie walczyć w pętli z czymś,
 * co ustawia zegar po swojemu.
 */
function NaprawaZegara({ groupId, mozeZapisywac }: { groupId: string; mozeZapisywac: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const dane = useQuery({ queryKey: ['zegar-naprawiaj', groupId], queryFn: () => fetchZegarNaprawiaj(groupId) });
  const [zapisuje, setZapisuje] = useState(false);
  const wlaczone = dane.data?.naprawiaj ?? false;
  const progZPortalu = dane.data?.progSekund ?? 10;
  const [prog, setProg] = useState('');
  /** Pole pokazuje wartość z portalu, dopóki nikt w nie nie wpisał — inaczej mrugałoby przy odświeżeniu. */
  const progWPolu = prog === '' ? String(progZPortalu) : prog;
  const progLiczba = Number(progWPolu);
  const progPoprawny = Number.isInteger(progLiczba) && progLiczba >= 5 && progLiczba <= 86_400;
  const progZmieniony = progPoprawny && progLiczba !== progZPortalu;

  const zapiszProg = async (): Promise<void> => {
    if (!progZmieniony) return;
    setZapisuje(true);
    try {
      const w = await setZegarNaprawiaj(groupId, wlaczone, progLiczba);
      toast.success(
        w.naprawiaj
          ? `Próg rozjazdu: ${w.progSekund} s — od tyle agent koryguje zegar`
          : `Próg rozjazdu: ${w.progSekund} s — korekta wyłączona, więc na razie tylko zgłaszanie`,
      );
      setProg('');
      void qc.invalidateQueries({ queryKey: ['zegar-naprawiaj'] });
      void qc.invalidateQueries({ queryKey: ['agents'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  const przelacz = async (): Promise<void> => {
    setZapisuje(true);
    try {
      const w = await setZegarNaprawiaj(groupId, !wlaczone);
      toast.success(w.naprawiaj ? 'Agent będzie korygował zegar maszyny' : 'Agent tylko mierzy zegar, nie zmienia go');
      void qc.invalidateQueries({ queryKey: ['zegar-naprawiaj'] });
      /** Lista agentów pokazuje ten stan przy rozjeździe — bez tego świeciłaby starą wartością. */
      void qc.invalidateQueries({ queryKey: ['agents'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-sm">
      <span className="text-xs text-muted-foreground">Korekta zegara maszyny przez agenta:</span>
      <Badge variant={wlaczone ? 'default' : 'neutral'}>{wlaczone ? 'włączona' : 'wyłączona'}</Badge>
      {mozeZapisywac && (
        <Button size="sm" variant="outline" className="h-7" disabled={zapisuje} onClick={() => void przelacz()}>
          {zapisuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {wlaczone ? 'Wyłącz' : 'Włącz'}
        </Button>
      )}
      {/**
        * PRÓG ROZJAZDU — druga połowa tej samej decyzji.
        *
        * ⚠️⚠️ DO 0.3.396 PORTAL ROZSTRZYGAŁ „CZY", ALE NIE „OD KIEDY": próg siedział w `agent.json`
        * na maszynie. Zobaczone na testówce PESA (26.08.2026): korekta włączona, rozjazd −29 s
        * i nic się nie działo, bo próg wynosił 300 s — a ekran nie pokazywał progu wcale, więc
        * wyglądało to na zepsutą korektę.
        */}
      <span className="ml-2 text-xs text-muted-foreground">Próg rozjazdu:</span>
      <input
        type="number"
        min={5}
        max={86400}
        value={progWPolu}
        onChange={(e) => setProg(e.target.value)}
        disabled={!mozeZapisywac || zapisuje}
        className="h-7 w-20 rounded border border-input bg-background px-2 text-xs tabular-nums"
        aria-label="Próg rozjazdu zegara w sekundach"
      />
      <span className="text-xs text-muted-foreground">s</span>
      {mozeZapisywac && progZmieniony && (
        <Button size="sm" variant="outline" className="h-7" disabled={zapisuje} onClick={() => void zapiszProg()}>
          {zapisuje && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          Zapisz próg
        </Button>
      )}
      {!progPoprawny && <span className="text-[11px] text-destructive">5–86 400 s</span>}
      <span className="text-[11px] text-muted-foreground">
        Agent przestawia zegar względem czasu portalu, gdy rozjazd przekroczy próg. Odmawia przy rozjeździe większym niż
        doba (to nie dryf, tylko padła bateria albo zły rok) i przestaje po trzech korektach pod rząd, żeby nie walczyć
        w pętli z czymś, co ustawia zegar po swojemu. Rozjazd jest mierzony w każdym cyklu niezależnie od progu, więc
        przy wyłączonej korekcie próg mówi tylko, od kiedy o nim pisać. Poniżej ~5 s pomiar zaczyna mierzyć opóźnienie
        sieci, a nie zegar.
      </span>
    </div>
  );
}

/**
 * POZOSTAŁE USTAWIENIA WYSPY — wszystko, co nie jest odstępem odpytywania tabeli SPS.
 *
 * ⚠️ OSOBNA SEKCJA, BO POPRZEDNIA WERSJA CHOWAŁA TE USTAWIENIA W TABELACH. Zgłoszone wprost:
 * „jest w sekcji TABELE SPS i trochę się to kupy nie trzyma, ledwo znalazłem to". Korekta zegara
 * i odstęp spisu kopii nie mają z tabelami SPS nic wspólnego poza tym, że jedno i drugie jedzie
 * do agenta tym samym kanałem — a to jest szczegół implementacji, nie sposób szukania na ekranie.
 */

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  KOMUNIKAT TRYBU KONSERWACJI — tekst, który zobaczy człowiek PRZY AUTOMACIE, gdy zasłonimy ekran.
 *
 *  ⚠️⚠️ PER WYSPA, A NIE JEDEN NA CAŁĄ INSTANCJĘ: „mamy zagranicznych userów i każdej firmie co
 *  innego musimy wpisać" (15.09.2026). Jedna instancja obsługuje kilka firm, więc polskie zdanie
 *  na maszynie u zagranicznego klienta byłoby gorsze niż jego brak.
 *
 *  ⚠️ PUSTE POLE ZNACZY „wróć do domyślnego", a nie „pokaż pusty ekran". Czarny ekran bez słowa
 *  wygląda jak zepsuty automat i człowiek zaczyna go szarpać, zamiast poczekać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
function KomunikatKonserwacji({ groupId, mozeZapisywac }: { groupId: string; mozeZapisywac: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const dane = useQuery({
    queryKey: ['konserwacja-komunikat', groupId],
    queryFn: () => fetchKomunikatKonserwacji(groupId),
  });
  const [wartosc, setWartosc] = useState('');
  const [zapisuje, setZapisuje] = useState(false);

  useEffect(() => {
    if (dane.data) setWartosc(dane.data.komunikat ?? '');
  }, [dane.data]);

  const zmienione = dane.data !== undefined && wartosc.trim() !== (dane.data.komunikat ?? '');

  const zapisz = async (): Promise<void> => {
    setZapisuje(true);
    try {
      const w = await setKomunikatKonserwacji(groupId, wartosc.trim() === '' ? null : wartosc.trim());
      toast.success(w.komunikat === null ? 'Wrócił komunikat domyślny' : 'Komunikat zapisany');
      void qc.invalidateQueries({ queryKey: ['konserwacja-komunikat'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <div className="space-y-2 rounded border border-border p-2 text-sm">
      <span className="text-xs text-muted-foreground">Komunikat na ekranie automatu w trybie konserwacji:</span>
      <Input
        className="h-8"
        maxLength={400}
        value={wartosc}
        placeholder={dane.data?.domyslny ?? ''}
        disabled={!mozeZapisywac || dane.isPending}
        onChange={(e) => setWartosc(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!mozeZapisywac || !zmienione || zapisuje} onClick={() => void zapisz()}>
          {zapisuje ? <Loader2 className="size-4 animate-spin" /> : 'Zapisz'}
        </Button>
        <span className="text-xs text-muted-foreground">Puste pole = tekst domyślny.</span>
      </div>
    </div>
  );
}

export function InneUstawieniaWyspy() {
  const { groupId } = useIslands();
  const { user } = useAuth();
  if (!groupId) return null;
  const mozeZapisywac = user?.role === 'ADMIN';

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div>
        <h2 className="font-semibold">Pozostałe ustawienia wyspy</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Ustawienia eksploatacyjne, które portal podaje agentom tej wyspy — obowiązują od najbliższego
          cyklu i nie wymagają wchodzenia na maszynę klienta.
        </p>
      </div>
      <NaprawaZegara groupId={groupId} mozeZapisywac={mozeZapisywac} />
      <OdstepKopii groupId={groupId} mozeZapisywac={mozeZapisywac} />
      <LimitRuchu groupId={groupId} mozeZapisywac={mozeZapisywac} />
      <KomunikatKonserwacji groupId={groupId} mozeZapisywac={mozeZapisywac} />
    </section>
  );
}
